import React from "react"
import { Link } from "gatsby"
import useWindowWidth from "../../../hooks/useWindowWidth"
import styled from "styled-components"
import { Container } from "@bw/bits"
import LayerMenu from "./LayerMenu"
import Logo from "./logo"
import LogoNoel from "./logo_noel"
import { Search } from "@bw/icons"
import { breakpoints } from "../../../theme"
import { useTranslation } from "react-i18next"

const HeaderWrapper = styled.div`
  position: ${props => (props.fixed ? "fixed" : "absolute")};
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  background: transparent;
  transition:
    background-color ease-in-out 0.25s,
    color ease-in-out 0.25s;
  height: 85px;
  padding: 12px 0;

  &.active {
    background: #fff;
  }

  @media (min-width: ${breakpoints.medium}px) {
    height: 156px;
  }
`

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 81px 1fr;

  @media (min-width: ${breakpoints.medium}px) {
    height: 156px;
  }
`

const StyledLink = styled(Link)`
  position: relative;
  text-decoration: none;
  color: currentColor;
  margin: 0px 40px 0 0;
  font-weight: 300;
  font-size: 16px;
  line-height: 25px;
  letter-spacing: 0.003em;
`

const MobileLink = styled(StyledLink)`
  font-size: 16px;
  line-height: 19px;
  padding: 30px 0 30px 30px;
  margin-left: -30px;
  position: relative;
  font-weight: 300;
  width: 100%;
  display: block;

  @media (min-width: ${breakpoints.medium}px) {
    padding: 47px 0 47px 30px;
  }

  @media (min-width: ${breakpoints.medium}px) and (min-height: 900px) {
    padding: 27px 0 27px 30px;
  }

  &:last-child {
    &:after {
      display: none;
    }
  }

  &:after {
    border-bottom: 1px solid var(--light-grey);
    content: "";
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
  }

  &.active {
    background: var(--lighter-grey);
    width: 100%;
    position: relative;

    &:before {
      content: "";
      display: block;
      width: 10px;
      height: 10px;
      position: absolute;

      top: 50%;
      left: 12px;
      margin-top: -4px;
      border-radius: 50%;
      background-color: var(--page-color);
    }
  }
`

const TopGrid = styled.div`
  display: flex;
  justify-content: flex-end;

  > :nth-child(1) {
    margin-right: 56px;

    @media (min-width: ${breakpoints.medium}px) {
      margin-right: 90px;
    }
  }
`

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: auto 40px;
  margin-top: 5px;

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: auto 72px;
    margin-top: 30px;
  }
`

const LogoWrapper = styled(Link)`
  display: block;
  transform: translateY(-1px);
  position: absolute;
  z-index: 6;

  @media (min-width: ${breakpoints.medium}px) {
    &:after,
    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 44px;
      border-bottom: 1px solid var(--light-grey);
      width: 80vw;
      right: 108px;
    }

    &:after {
      left: 177px;
    }
  }

  svg {
    height: 63px;
    width: 81px;

    @media (min-width: ${breakpoints.medium}px) {
      height: 126px;
      width: 162px;
    }
  }
`

const MainMenu = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  > * {
    display: none;
    &:before {
      content: "";
      width: 0px;
      right: 0px;
      bottom: -3px;
      position: absolute;
      height: 1.5px;
      background-color: var(--font-color);
      transition: width 0.6s cubic-bezier(0.25, 1, 0.5, 1);
    }
    &:hover::before {
      left: 0;
      right: auto;
      width: 100%;
    }
  }
  > :nth-child(1) {
    display: block;
  }
  @media (min-width: ${breakpoints.medium}px) {
    > :nth-child(1),
    > :nth-child(2),
    > :nth-child(3) {
      display: block;
    }
  }
`

const Burger = styled.button`
  border: 0;
  background: none;
  padding: 0;
  display: flex;
  justify-content: end;
  align-items: center;

  @media (min-width: ${breakpoints.medium}px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: end;
  }
`

const HamburgerIcon = styled.a`
  width: 33px;
  height: fit-content;
  display: block;

  > .line {
    width: 33px;
    background: var(--font-color);
    margin: 6px 0;
    height: 2px;
    display: block;
    transition: all 600ms cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  &:hover {
    > .line-1 {
      width: 30px;
    }
    > .line-2 {
      width: 40px;
    }
    > .line-3 {
      width: 20px;
    }
  }
`

const ButtonECommande = styled.a`
  font-family: "Ginto", georgia, serif;
  font-weight: 500;
  font-size: 10px;
  line-height: 16px;
  text-decoration: none;
  max-height: 32px;
  border: none;
  align-self: center;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  padding: 10px 13px;
  background-color: var(--brand-p-plus);
  transition: background-color 0.3s ease-in-out;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 13px;
    line-height: 18px;
    padding: 10px 25px;
  }

  &:hover {
    background-color: var(--brand-dark-green);
  }
`

const StyledSearch = styled(Link)`
  padding: 0 5px 0 0;
  align-self: center;
`

const Title = styled.div`
  font-family: "Ginto", georgia, serif;
  font-weight: 500;
  margin: 10px 0;
  font-size: 25px;

  @media (min-width: ${breakpoints.medium}px) {
    margin: 20px 0 25px 0;
    font-size: 32px;
  }
`

const Links = ({ mode, menu, direction }) => {
  const MenuLink = mode === "desktop" ? StyledLink : MobileLink
  return menu.map((item, i) => (
    <MenuLink activeClassName="active" key={i} to={item.to}>
      {item.label}
    </MenuLink>
  ))
}

const Header = ({
  menu,
  menuLayer,
  negative,
  fixedHeader = true,
  orderUrl = "https://fr.abilis.ch/",
}) => {
  const windowWidth = useWindowWidth()
  const [navBar, setNavBar] = React.useState(false)
  const [show, setShow] = React.useState(false)
  const headerRef = React.useRef(null)
  const { t } = useTranslation()

  React.useEffect(() => {
    const changeBackground = () => {
      setNavBar(window.scrollY >= 20)
    }

    changeBackground()
    window.addEventListener("scroll", changeBackground, { passive: true })

    return () => {
      window.removeEventListener("scroll", changeBackground)
    }
  }, [setNavBar])

  const menuLayerSolved =
    windowWidth < 768 ? [...menu.slice(1), ...menuLayer] : menuLayer

  return (
    <>
      <HeaderWrapper
        id="header"
        ref={headerRef}
        negative={negative}
        fixed={fixedHeader}
        className={navBar ? "active" : null}
      >
        <Container>
          <HeaderContainer>
            <div>
              <LogoWrapper to="/">
                {
                /* Affichage du logo normal*/
                <Logo />
                
                /* Affichage du logo de Noel */  
                /*<LogoNoel />*/
                }
              </LogoWrapper>
            </div>
            <div>
              <TopGrid>
                <ButtonECommande
                  href={orderUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("menu.ecommande")}
                </ButtonECommande>
                <StyledSearch to="/recherche">
                  <Search />
                </StyledSearch>
              </TopGrid>
              <BottomGrid>
                <MainMenu>
                  <Links mode="desktop" menu={menu} />
                </MainMenu>
                <Burger
                  role="button"
                  tabIndex="0"
                  onKeyDown={() => {
                    setShow(true)
                  }}
                  onClick={() => {
                    setShow(true)
                  }}
                >
                  <HamburgerIcon>
                    <span className="line line-1"></span>
                    <span className="line line-2"></span>
                    <span className="line line-3"></span>
                  </HamburgerIcon>
                </Burger>
              </BottomGrid>
            </div>
          </HeaderContainer>
        </Container>
      </HeaderWrapper>
      <LayerMenu show={show} setShow={setShow}>
        <Title>Menu</Title>
        <Links mode="mobile" menu={menuLayerSolved} />
      </LayerMenu>
    </>
  )
}

export { Header }
